
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import tradingQuotationService from "../services/tradingQuotationService";
import tradingCostingService from "../services/tradingCostingService";
import tradingEnquiryService from "../services/tradingEnquiryService";
import axiosClient from "../../../api/axiosClient";
const unwrap = (response) =>
  response?.data?.data ?? response?.data ?? {};

const unwrapList = (response) => {
  const data = unwrap(response);

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.costings)) return data.costings;
  if (Array.isArray(data?.enquiries)) return data.enquiries;

  return [];
};

const numberValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return value;
};

const amount = (value, currency = "INR") => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numberValue(value));
  } catch {
    return numberValue(value).toFixed(2);
  }
};

const today = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getCustomerId = (enquiry) =>
  enquiry?.business_partner_id ??
  enquiry?.customer_id ??
  enquiry?.business_partner?.id ??
  "";

const getCustomerName = (enquiry) =>
  enquiry?.business_partner_name ??
  enquiry?.customer_name ??
  enquiry?.business_partner?.business_partner_name ??
  enquiry?.business_partner?.name ??
  "";

const getEnquiryLabel = (enquiry) =>
  enquiry?.enquiry_no ??
  enquiry?.enquiry_number ??
  `Enquiry #${enquiry?.id}`;

const getCostingLabel = (costing) =>
  costing?.costing_no ??
  `Costing #${costing?.id}`;

const isApprovedCurrentCosting = (costing) =>
  String(costing?.status_code || "").toUpperCase() === "APPROVED" &&
  costing?.is_current_version !== false &&
  costing?.is_current_version !== 0;

const getItemName = (item) =>
  item?.material_name ??
  item?.item_description ??
  item?.material_code ??
  `Item #${item?.id ?? ""}`;

const getQuantity = (item) =>
  numberValue(item?.quantity);

const getUnitPrice = (item) =>
  numberValue(
    item?.selling_unit_price ??
    item?.unit_selling_price ??
    item?.selling_price ??
    item?.unit_price
  );

const getLineAmount = (item) => {
  const savedAmount =
    item?.selling_total_amount ??
    item?.selling_line_amount ??
    item?.line_total ??
    item?.total_amount;

  if (savedAmount !== null && savedAmount !== undefined) {
    return numberValue(savedAmount);
  }

  return getQuantity(item) * getUnitPrice(item);
};

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f7f8fc",
    padding: "24px",
    fontFamily: "Inter, Arial, sans-serif",
    color: "#20243a",
  },
  container: {
    maxWidth: "1440px",
    margin: "0 auto",
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px",
    marginBottom: "24px",
  },
  title: {
    margin: 0,
    fontSize: "26px",
    fontWeight: 750,
    letterSpacing: "-0.5px",
  },
  subtitle: {
    margin: "7px 0 0",
    color: "#737991",
    fontSize: "13px",
  },
  card: {
    background: "#ffffff",
    border: "1px solid #e9eaf2",
    borderRadius: "14px",
    padding: "22px",
    marginBottom: "20px",
    boxShadow: "0 3px 12px rgba(32,36,58,0.025)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "20px",
  },
  sectionTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: 750,
  },
  sectionSubtitle: {
    margin: "5px 0 0",
    fontSize: "12px",
    color: "#858ba0",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "18px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "7px",
    minWidth: 0,
  },
  label: {
    fontSize: "12px",
    fontWeight: 650,
    color: "#525a73",
  },
  input: {
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    height: "42px",
    border: "1px solid #dfe2ed",
    borderRadius: "8px",
    padding: "0 12px",
    background: "#ffffff",
    color: "#292d43",
    fontSize: "13px",
    outlineColor: "#6254d9",
  },
  readOnly: {
    background: "#f7f8fc",
    color: "#60677e",
  },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: "94px",
    border: "1px solid #dfe2ed",
    borderRadius: "8px",
    padding: "11px 12px",
    fontFamily: "inherit",
    fontSize: "13px",
    resize: "vertical",
    outlineColor: "#6254d9",
  },
  button: {
    height: "40px",
    padding: "0 17px",
    borderRadius: "8px",
    border: "1px solid #dedfec",
    background: "#ffffff",
    color: "#4e536b",
    fontWeight: 650,
    fontSize: "13px",
    cursor: "pointer",
  },
  primaryButton: {
    height: "42px",
    padding: "0 20px",
    borderRadius: "8px",
    border: "none",
    background: "#6254d9",
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "13px",
    cursor: "pointer",
    boxShadow: "0 4px 10px rgba(98,84,217,0.18)",
  },
  disabledButton: {
    opacity: 0.55,
    cursor: "not-allowed",
  },
  tableWrap: {
    width: "100%",
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "760px",
  },
  th: {
    padding: "12px 14px",
    textAlign: "left",
    background: "#f7f8fc",
    color: "#717890",
    fontSize: "11px",
    fontWeight: 750,
    borderBottom: "1px solid #e8eaf2",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "13px 14px",
    fontSize: "12px",
    color: "#454b63",
    borderBottom: "1px solid #eff0f5",
    verticalAlign: "middle",
  },
  right: {
    textAlign: "right",
    whiteSpace: "nowrap",
  },
  badge: {
    display: "inline-block",
    padding: "5px 9px",
    borderRadius: "20px",
    background: "#edf8f1",
    color: "#27804d",
    fontSize: "10px",
    fontWeight: 750,
  },
  mutedBadge: {
    display: "inline-block",
    padding: "5px 9px",
    borderRadius: "20px",
    background: "#f0efff",
    color: "#6254d9",
    fontSize: "10px",
    fontWeight: 750,
  },
  empty: {
    padding: "30px 16px",
    textAlign: "center",
    color: "#858ba0",
    fontSize: "13px",
  },
  alert: {
    padding: "12px 14px",
    marginBottom: "18px",
    borderRadius: "9px",
    background: "#fff0ef",
    border: "1px solid #ffd9d5",
    color: "#b42318",
    fontSize: "13px",
  },
  info: {
    padding: "12px 14px",
    marginBottom: "18px",
    borderRadius: "9px",
    background: "#f1f0ff",
    border: "1px solid #e0ddff",
    color: "#5549bb",
    fontSize: "12px",
    lineHeight: 1.6,
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: "12px",
  },
  summaryItem: {
    padding: "16px",
    background: "#f8f9fd",
    border: "1px solid #eceef5",
    borderRadius: "10px",
  },
  summaryLabel: {
    fontSize: "11px",
    color: "#7b8298",
    marginBottom: "8px",
  },
  summaryValue: {
    fontSize: "18px",
    fontWeight: 750,
    color: "#292d43",
    overflowWrap: "anywhere",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    padding: "16px 0 4px",
  },
};

function Field({ label, children }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function SectionHeading({ title, subtitle, right }) {
  return (
    <div style={styles.sectionHeader}>
      <div>
        <h2 style={styles.sectionTitle}>{title}</h2>
        {subtitle ? (
          <p style={styles.sectionSubtitle}>{subtitle}</p>
        ) : null}
      </div>
      {right}
    </div>
  );
}

export default function TradingQuotationFormPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const requestedCostingId = searchParams.get("costing_id");

  const [enquiries, setEnquiries] = useState([]);
  const [costings, setCostings] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");

  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedEnquiryId, setSelectedEnquiryId] = useState("");
  const [selectedCostingId, setSelectedCostingId] = useState("");

  const [costing, setCosting] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [loadingCosting, setLoadingCosting] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");

  const [form, setForm] = useState({
    quotation_date: today(),
    valid_until: "",
    subject: "",
    customer_reference: "",
    remarks: "",
  });

  useEffect(() => {
    let active = true;

    const loadOptions = async () => {
    setLoadingOptions(true);
    setError("");

    try {
      const [enquiryResponse, costingResponse, departmentResponse] =
        await Promise.all([
          tradingEnquiryService.getEnquiries(),
          tradingCostingService.getCostings(),
          //axiosClient.get("/tenant/api/departments/list"),
          axiosClient.get("/api/trading/quotations/departments"),
        ]);

      if (!active) return;

      setEnquiries(unwrapList(enquiryResponse));

      const allCostings = unwrapList(costingResponse);
      setCostings(allCostings.filter(isApprovedCurrentCosting));

      setDepartments(departmentResponse.data?.data || []);
    } catch (err) {
      if (!active) return;

      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Unable to load Trading Enquiries, approved Costings, and Departments."
      );
    } finally {
      if (active) setLoadingOptions(false);
    }
  };

    loadOptions();

    return () => {
      active = false;
    };
  }, []);

  const customers = useMemo(() => {
    const unique = new Map();

    enquiries.forEach((enquiry) => {
      const customerId = getCustomerId(enquiry);

      if (customerId === "" || customerId === null) return;

      const key = String(customerId);

      if (!unique.has(key)) {
        unique.set(key, {
          id: key,
          name: getCustomerName(enquiry),
        });
      } else if (
        !unique.get(key).name &&
        getCustomerName(enquiry)
      ) {
        unique.get(key).name = getCustomerName(enquiry);
      }
    });

    return Array.from(unique.values()).sort((a, b) =>
      String(a.name || a.id).localeCompare(String(b.name || b.id))
    );
  }, [enquiries]);

  const filteredEnquiries = useMemo(() => {
    if (!selectedCustomerId) return enquiries;

    return enquiries.filter(
      (enquiry) =>
        String(getCustomerId(enquiry)) === String(selectedCustomerId)
    );
  }, [enquiries, selectedCustomerId]);

  const filteredCostings = useMemo(() => {
    return costings.filter((row) => {
      if (
        selectedEnquiryId &&
        String(row.enquiry_id) !== String(selectedEnquiryId)
      ) {
        return false;
      }

      return true;
    });
  }, [costings, selectedEnquiryId]);

  const selectedEnquiry = useMemo(
    () =>
      enquiries.find(
        (row) => String(row.id) === String(selectedEnquiryId)
      ) || null,
    [enquiries, selectedEnquiryId]
  );

  const currency = costing?.currency_code || "INR";

  const items = Array.isArray(costing?.items) ? costing.items : [];
  const charges = Array.isArray(costing?.charges) ? costing.charges : [];
  const terms = Array.isArray(costing?.terms) ? costing.terms : [];

  const customerCharges = charges.filter(
    (charge) =>
      String(charge?.payer_type || "").toUpperCase() === "CUSTOMER"
  );

  const companyAndVendorCharges = charges.filter(
    (charge) =>
      String(charge?.payer_type || "").toUpperCase() !== "CUSTOMER"
  );

  const customerChargeAmount = customerCharges.reduce(
    (total, charge) => total + numberValue(charge.charge_amount),
    0
  );

  const customerChargeTax = customerCharges.reduce((total, charge) => {
    if (
      charge?.gst_amount !== undefined &&
      charge?.gst_amount !== null
    ) {
      return total + numberValue(charge.gst_amount);
    }

    return (
      total +
      (numberValue(charge.charge_amount) *
        numberValue(charge.gst_rate)) /
        100
    );
  }, 0);

  const itemAmount = items.reduce(
    (total, item) => total + getLineAmount(item),
    0
  );

  const estimatedCustomerTotal =
    numberValue(costing?.net_amount) ||
    itemAmount + customerChargeAmount + customerChargeTax;

  const loadCosting = async (costingId) => {
    if (!costingId) {
      setCosting(null);
      setSelectedCostingId("");
      return;
    }

    const selected = costings.find(
      (row) => String(row.id) === String(costingId)
    );

    if (!selected) {
      setError("Please select an approved, current Trading Costing.");
      return;
    }

    setLoadingCosting(true);
    setError("");
    setCosting(null);
    setSelectedCostingId(String(costingId));

    try {
      const response = await tradingCostingService.getCosting(costingId);
      const detail = unwrap(response);

      if (!detail?.id) {
        throw new Error("The selected costing could not be loaded.");
      }

      if (!isApprovedCurrentCosting(detail)) {
        throw new Error(
          "Only an approved, current costing can be used to create a quotation."
        );
      }

      const linkedEnquiryId = detail.enquiry_id ?? selected.enquiry_id;

      setCosting(detail);

      if (linkedEnquiryId !== undefined && linkedEnquiryId !== null) {
        setSelectedEnquiryId(String(linkedEnquiryId));

        const linkedEnquiry = enquiries.find(
          (row) => String(row.id) === String(linkedEnquiryId)
        );

        if (linkedEnquiry) {
          const customerId = getCustomerId(linkedEnquiry);

          setSelectedCustomerId(
            customerId === "" || customerId === null
              ? ""
              : String(customerId)
          );
        } else {
          setSelectedCustomerId(
            detail.business_partner_id
              ? String(detail.business_partner_id)
              : ""
          );
        }
      }
    } catch (err) {
      setCosting(null);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to load the selected costing."
      );
    } finally {
      setLoadingCosting(false);
    }
  };

  useEffect(() => {
    if (!requestedCostingId || loadingOptions || !costings.length) return;

    const matchingCosting = costings.find(
      (row) => String(row.id) === String(requestedCostingId)
    );

    if (matchingCosting) {
      loadCosting(requestedCostingId);
    } else {
      setError(
        "The costing provided in the link is not an approved current costing."
      );
    }
    // Run for the initial URL preselection only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedCostingId, loadingOptions]);

  const handleCustomerChange = (value) => {
    setSelectedCustomerId(value);
    setSelectedEnquiryId("");
    setSelectedCostingId("");
    setCosting(null);
    setError("");
  };

  const handleEnquiryChange = (value) => {
    setSelectedEnquiryId(value);
    setSelectedCostingId("");
    setCosting(null);
    setError("");

    const enquiry = enquiries.find(
      (row) => String(row.id) === String(value)
    );

    const customerId = getCustomerId(enquiry);

    if (customerId !== "" && customerId !== null) {
      setSelectedCustomerId(String(customerId));
    }
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    setError("");

    if (!selectedCostingId || !costing) {
      setError("Select an approved Trading Costing before creating the quotation.");
      return;
    }

    if (
      form.valid_until &&
      form.quotation_date &&
      form.valid_until < form.quotation_date
    ) {
      setError("Valid Until cannot be earlier than the Quotation Date.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        costing_id: Number(selectedCostingId),
        department_id: Number(selectedDepartmentId),
        enquiry_id: Number(costing.enquiry_id),
        business_partner_id: Number(
          costing.business_partner_id ??
            getCustomerId(selectedEnquiry) ??
            selectedCustomerId
        ),
        quotation_date: form.quotation_date,
        valid_until: form.valid_until || null,
        subject: form.subject.trim(),
        customer_reference: form.customer_reference.trim(),
        remarks: form.remarks.trim(),
      };

      if (!payload.business_partner_id) {
        delete payload.business_partner_id;
      }

      const response = await tradingQuotationService.createQuotation(payload);
      const created = unwrap(response);

      const quotationId =
        created?.id ??
        created?.quotation?.id ??
        created?.data?.id;

      if (!quotationId) {
        throw new Error(
          "Quotation creation returned no quotation ID. Please check the quotation list before retrying."
        );
      }

      navigate(`/trading/quotations/${quotationId}`);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Unable to create the quotation."
      );
    } finally {
      setSaving(false);
    }
  };

  const customerDisplay = (() => {
    if (costing?.business_partner_id) {
      const match = customers.find(
        (row) =>
          String(row.id) === String(costing.business_partner_id)
      );

      return match?.name
        ? `${match.name} (ID: ${costing.business_partner_id})`
        : `Customer ID: ${costing.business_partner_id}`;
    }

    const enquiry = selectedEnquiry;
    const customerId = getCustomerId(enquiry);
    const customerName = getCustomerName(enquiry);

    if (customerName) return customerName;
    if (customerId) return `Customer ID: ${customerId}`;

    return "";
  })();

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.topBar}>
          <div>
            <h1 style={styles.title}>Create Trading Quotation</h1>
            <p style={styles.subtitle}>
              Prepare a customer quotation from an approved Trading Costing.
            </p>
          </div>

          <button
            type="button"
            style={styles.button}
            onClick={() => navigate("/trading/quotations")}
          >
            ← Back to Quotations
          </button>
        </div>

        {error ? (
          <div role="alert" style={styles.alert}>
            {error}
          </div>
        ) : null}

        <div style={styles.info}>
          <strong>Source rule:</strong> The quotation is created from an
          approved, current Trading Costing. The costing remains the source
          for items, pricing, charges and terms. Only customer-payable charges
          contribute to the customer-facing quotation total.
        </div>

        <form onSubmit={handleCreate}>
          <section style={styles.card}>
            <SectionHeading
              title="Quotation Header"
              subtitle="Select the customer enquiry and its approved costing."
            />

            <div style={styles.grid}>
              <Field label="Customer">
                <select
                  style={styles.input}
                  value={selectedCustomerId}
                  onChange={(event) =>
                    handleCustomerChange(event.target.value)
                  }
                  disabled={loadingOptions || saving}
                >
                  <option value="">Select Customer</option>
                  {customers.map((customer) => (
                    <option key={customer.id} value={customer.id}>
                      {customer.name
                        ? `${customer.name} (ID: ${customer.id})`
                        : `Customer #${customer.id}`}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Trading Enquiry">
                <select
                  style={styles.input}
                  value={selectedEnquiryId}
                  onChange={(event) =>
                    handleEnquiryChange(event.target.value)
                  }
                  disabled={loadingOptions || saving}
                >
                  <option value="">Select Enquiry</option>
                  {filteredEnquiries.map((enquiry) => (
                    <option key={enquiry.id} value={enquiry.id}>
                      {getEnquiryLabel(enquiry)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Approved Trading Costing">
                <select
                  style={styles.input}
                  value={selectedCostingId}
                  onChange={(event) => loadCosting(event.target.value)}
                  disabled={loadingOptions || loadingCosting || saving}
                  required
                >
                  <option value="">Select Costing</option>
                  {filteredCostings.map((row) => (
                    <option key={row.id} value={row.id}>
                      {getCostingLabel(row)}
                      {row.version_no
                        ? ` · Version ${row.version_no}`
                        : ""}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Department">
                <select
                  style={styles.input}
                  value={selectedDepartmentId}
                  onChange={(event) =>
                    setSelectedDepartmentId(event.target.value)
                  }
                  disabled={loadingOptions || saving}
                  required
                >
                  <option value="">Select Department</option>

                  {departments.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name ||
                        department.department_name ||
                        `Department #${department.id}`}
                    </option>
                  ))}
                </select>
              </Field>    
              <Field label="Quotation Date">
                <input
                  type="date"
                  name="quotation_date"
                  value={form.quotation_date}
                  onChange={handleFormChange}
                  style={styles.input}
                  required
                  disabled={saving}
                />
              </Field>

              <Field label="Valid Until">
                <input
                  type="date"
                  name="valid_until"
                  value={form.valid_until}
                  onChange={handleFormChange}
                  min={form.quotation_date || undefined}
                  style={styles.input}
                  disabled={saving}
                />
              </Field>

              <Field label="Customer Reference">
                <input
                  type="text"
                  name="customer_reference"
                  value={form.customer_reference}
                  onChange={handleFormChange}
                  style={styles.input}
                  placeholder="Customer RFQ / reference number"
                  maxLength={100}
                  disabled={saving}
                />
              </Field>

              <Field label="Subject">
                <input
                  type="text"
                  name="subject"
                  value={form.subject}
                  onChange={handleFormChange}
                  style={styles.input}
                  placeholder="Quotation subject"
                  maxLength={255}
                  disabled={saving}
                />
              </Field>

              <Field label="Linked Customer (from source)">
                <input
                  type="text"
                  value={customerDisplay}
                  readOnly
                  style={{ ...styles.input, ...styles.readOnly }}
                  placeholder="Populated from selected costing"
                />
              </Field>
            </div>

            <div style={{ marginTop: "18px" }}>
              <Field label="Remarks">
                <textarea
                  name="remarks"
                  value={form.remarks}
                  onChange={handleFormChange}
                  style={styles.textarea}
                  placeholder="Additional quotation remarks"
                  disabled={saving}
                />
              </Field>
            </div>

            {loadingOptions ? (
              <p style={styles.sectionSubtitle}>
                Loading enquiries and approved costings…
              </p>
            ) : null}

            {loadingCosting ? (
              <p style={styles.sectionSubtitle}>
                Loading costing details…
              </p>
            ) : null}

            {!loadingOptions && costings.length === 0 ? (
              <p style={styles.sectionSubtitle}>
                No approved current Trading Costings were returned. Approve a
                costing before creating a quotation.
              </p>
            ) : null}
          </section>

          <section style={styles.card}>
            <SectionHeading
              title="Items & Pricing"
              subtitle="Read-only preview of the selected costing. Pricing is not recalculated here."
              right={
                costing ? (
                  <span style={styles.badge}>Source Costing Loaded</span>
                ) : null
              }
            />

            {!costing ? (
              <div style={styles.empty}>
                Select an approved costing to preview its items.
              </div>
            ) : items.length === 0 ? (
              <div style={styles.empty}>
                The selected costing does not contain any items.
              </div>
            ) : (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>#</th>
                      <th style={styles.th}>Material / Description</th>
                      <th style={styles.th}>HSN</th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        Quantity
                      </th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        Unit Selling Price
                      </th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        Line Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={item.id ?? `${item.material_code}-${index}`}>
                        <td style={styles.td}>{index + 1}</td>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 700, color: "#30354d" }}>
                            {getItemName(item)}
                          </div>
                          {item.material_code ? (
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#8a90a3",
                                marginTop: "4px",
                              }}
                            >
                              Code: {item.material_code}
                            </div>
                          ) : null}
                        </td>
                        <td style={styles.td}>
                          {displayValue(item.hsn_code)}
                        </td>
                        <td style={{ ...styles.td, ...styles.right }}>
                          {getQuantity(item).toLocaleString("en-IN")}
                        </td>
                        <td style={{ ...styles.td, ...styles.right }}>
                          {amount(getUnitPrice(item), currency)}
                        </td>
                        <td
                          style={{
                            ...styles.td,
                            ...styles.right,
                            fontWeight: 700,
                          }}
                        >
                          {amount(getLineAmount(item), currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {costing ? (
              <p style={{ ...styles.sectionSubtitle, marginTop: "12px" }}>
                The quotation backend copies item values from the selected
                costing. This preview does not update costing prices.
              </p>
            ) : null}
          </section>

          <section style={styles.card}>
            <SectionHeading
              title="Additional Charges"
              subtitle="Customer-payable charges affect the quotation. Company- and vendor-payable charges are shown for traceability."
            />

            {!costing ? (
              <div style={styles.empty}>
                Select a costing to preview additional charges.
              </div>
            ) : charges.length === 0 ? (
              <div style={styles.empty}>
                No additional charges are configured in this costing.
              </div>
            ) : (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Charge</th>
                      <th style={styles.th}>Payer</th>
                      <th style={styles.th}>Remarks</th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        Charge Amount
                      </th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        GST Rate
                      </th>
                      <th style={{ ...styles.th, ...styles.right }}>
                        GST Amount
                      </th>
                      <th style={styles.th}>Quotation Treatment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {charges.map((charge, index) => {
                      const payer = String(
                        charge?.payer_type || ""
                      ).toUpperCase();

                      const isCustomerCharge = payer === "CUSTOMER";

                      const tax =
                        charge?.gst_amount !== undefined &&
                        charge?.gst_amount !== null
                          ? numberValue(charge.gst_amount)
                          : (numberValue(charge.charge_amount) *
                              numberValue(charge.gst_rate)) /
                            100;

                      return (
                        <tr key={charge.id ?? `charge-${index}`}>
                          <td style={styles.td}>
                            {displayValue(charge.charge_type)}
                          </td>
                          <td style={styles.td}>
                            {displayValue(payer)}
                          </td>
                          <td style={styles.td}>
                            {displayValue(charge.remarks)}
                          </td>
                          <td style={{ ...styles.td, ...styles.right }}>
                            {amount(charge.charge_amount, currency)}
                          </td>
                          <td style={{ ...styles.td, ...styles.right }}>
                            {numberValue(charge.gst_rate).toFixed(2)}%
                          </td>
                          <td style={{ ...styles.td, ...styles.right }}>
                            {amount(tax, currency)}
                          </td>
                          <td style={styles.td}>
                            {isCustomerCharge ? (
                              <span style={styles.badge}>
                                Included
                              </span>
                            ) : (
                              <span style={styles.mutedBadge}>
                                Excluded from customer total
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {costing ? (
              <div style={{ ...styles.summaryGrid, marginTop: "18px" }}>
                <div style={styles.summaryItem}>
                  <div style={styles.summaryLabel}>
                    Customer-payable charges
                  </div>
                  <div style={styles.summaryValue}>
                    {amount(customerChargeAmount, currency)}
                  </div>
                </div>

                <div style={styles.summaryItem}>
                  <div style={styles.summaryLabel}>
                    GST on customer-payable charges
                  </div>
                  <div style={styles.summaryValue}>
                    {amount(customerChargeTax, currency)}
                  </div>
                </div>

                <div style={styles.summaryItem}>
                  <div style={styles.summaryLabel}>
                    Other charges (excluded)
                  </div>
                  <div style={styles.summaryValue}>
                    {amount(
                      companyAndVendorCharges.reduce(
                        (total, charge) =>
                          total + numberValue(charge.charge_amount),
                        0
                      ),
                      currency
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </section>

          <section style={styles.card}>
            <SectionHeading
              title="Terms & Conditions"
              subtitle="Terms are displayed from the source costing. The quotation receives its own snapshot when created."
            />

            {!costing ? (
              <div style={styles.empty}>
                Select a costing to preview its terms and conditions.
              </div>
            ) : terms.length === 0 ? (
              <div style={styles.empty}>
                No terms and conditions were returned for this costing.
              </div>
            ) : (
              <div style={{ display: "grid", gap: "10px" }}>
                {terms.map((term, index) => (
                  <div
                    key={term.id ?? term.term_master_id ?? `term-${index}`}
                    style={{
                      padding: "13px 15px",
                      background: "#fafaff",
                      border: "1px solid #ecebfa",
                      borderRadius: "9px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 750,
                        color: "#454b63",
                        marginBottom: "6px",
                      }}
                    >
                      {term.term_name ??
                        term.name ??
                        term.title ??
                        `Term ${index + 1}`}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        lineHeight: 1.7,
                        color: "#737991",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {displayValue(
                        term.value ??
                          term.term_value ??
                          term.description
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section style={styles.card}>
            <SectionHeading
              title="Quotation Summary"
              subtitle="Reference preview only. The backend remains authoritative for saved quotation totals."
            />

            <div style={styles.summaryGrid}>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Source Costing</div>
                <div style={styles.summaryValue}>
                  {costing ? getCostingLabel(costing) : "—"}
                </div>
              </div>

              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Items</div>
                <div style={styles.summaryValue}>{items.length}</div>
              </div>

              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Customer-payable charges</div>
                <div style={styles.summaryValue}>
                  {amount(customerChargeAmount, currency)}
                </div>
              </div>

              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Source Costing Total</div>
                <div style={styles.summaryValue}>
                  {costing
                    ? amount(estimatedCustomerTotal, currency)
                    : "—"}
                </div>
              </div>
            </div>
          </section>

          <div style={styles.footer}>
            <button
              type="button"
              style={styles.button}
              onClick={() => navigate("/trading/quotations")}
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={
                saving ||
                loadingOptions ||
                loadingCosting ||
                !costing ||
                !selectedCostingId
              }
              style={{
                ...styles.primaryButton,
                ...(saving ||
                loadingOptions ||
                loadingCosting ||
                !costing ||
                !selectedCostingId
                  ? styles.disabledButton
                  : {}),
              }}
            >
              {saving ? "Creating Quotation…" : "Create Quotation"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
